/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { BuildOutlined, DownOutlined, GroupOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { NodeGroup } from '@/client';
import { BackButton } from '@/components/Buttons/BackButton';
import { GuardedButton } from '@/components/GuardedButton';
import { PageHeader } from '@/components/PageHeader';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { Dropdown, MenuProps } from '@/primitives/Dropdown';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Link } from '@/primitives/Link';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { VerticalDivider } from '@/primitives/VerticalDivider';
import { tokens } from '@/theme';

import './NodeGroupDetailsHeader.css';

export type NodeGroupDetailsHeaderProps = {
  nodeGroup: NodeGroup;
  tenantId: string;
  isEditing: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  formName: string;
  formDescription: string;
  configName: string | undefined;
  revisionNumber: number | undefined;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  onAddNodes: () => void;
  onToggleNodeMode: () => void;
  onToggleNodeState: () => void;
  onApplyConfiguration: () => void;
  onUpdateFleetConfig: () => void;
  isApplyingConfig: boolean;
};

export function NodeGroupDetailsHeader({
  nodeGroup,
  tenantId,
  isEditing,
  isUpdating,
  isDeleting,
  formName,
  formDescription,
  configName,
  revisionNumber,
  onEdit,
  onCancel,
  onSave,
  onDelete,
  onAddNodes,
  onToggleNodeMode,
  onToggleNodeState,
  onApplyConfiguration,
  onUpdateFleetConfig,
  isApplyingConfig,
}: NodeGroupDetailsHeaderProps) {
  const { t } = useMimicTranslation('fleets');

  const hasChanges = formName !== nodeGroup.name || formDescription !== (nodeGroup.description || '');

  return (
    <>
      <Container style={{ marginBottom: '1.5em' }}>
        <BackButton to={`/tenants/${tenantId}/fleet-management`} label={t('backToFleets')} />
      </Container>
      {isEditing ? (
        <Container style={{ marginBottom: '1.5em' }}>
          <PageHeader
            title={
              <Flex gap="sm" align="center">
                {t('editFleetDetails')}
              </Flex>
            }
            level={3}
            fontWeight="600"
          >
            <Flex gap="sm">
              <Button dataTestId="cancel-edit" type="text" disabled={isUpdating} onClick={onCancel}>
                {t('cancel')}
              </Button>
              <Button dataTestId="delete-group" type="default" disabled={isUpdating || isDeleting} onClick={onDelete}>
                <span style={{ color: tokens.color.red.red07 }}>{t('deleteFleet')}</span>
              </Button>
              <Button dataTestId="add-nodes" type="default" disabled={isUpdating} onClick={onAddNodes}>
                {t('addNodes')}
              </Button>
              <Button
                dataTestId="save-group"
                type={hasChanges ? 'primary' : 'default'}
                disabled={!hasChanges || isUpdating}
                loading={isUpdating}
                onClick={onSave}
              >
                {t('save')}
              </Button>
            </Flex>
          </PageHeader>
        </Container>
      ) : (
        <div style={{ marginBottom: '1.5em' }}>
          <PageHeader
            icon={<GroupOutlined style={{ height: '16px', width: '16px' }} />}
            title={
              <Text type="subtle" size="md" strong style={{ lineHeight: '28px' }}>
                {t('fleetDetails')}
              </Text>
            }
            level={5}
            fontWeight="300"
          />
          <Flex justify="space-between" align="flex-start">
            <Flex vertical align="left" gap="xs">
              <Title level={4} style={{ fontWeight: 500, margin: 0 }}>
                {nodeGroup.name}
              </Title>
              <Flex gap="xs" wrap>
                <Text size="sm" type="subtle" style={{ textTransform: 'capitalize' }} data-testid="node-group-os">
                  <OsIcon name={nodeGroup.operatingSystem} /> {t(nodeGroup.operatingSystem)}
                </Text>
                <VerticalDivider />
                <Text mono copyable type="subtle" size="sm" data-testid="node-group-id">
                  {nodeGroup.id}
                </Text>
              </Flex>
            </Flex>
            <Flex gap="sm">
              <GuardedButton requiredRole="admin" dataTestId="edit-group" type="default" onClick={onEdit}>
                {t('editFleet')}
              </GuardedButton>
              <ActionsDropdown
                onToggleNodeMode={onToggleNodeMode}
                onToggleNodeState={onToggleNodeState}
                onApplyConfiguration={onApplyConfiguration}
                onUpdateFleetConfig={onUpdateFleetConfig}
                isApplyingConfig={isApplyingConfig}
              />
            </Flex>
          </Flex>
        </div>
      )}
      <Flex>
        <Container gap="xs" style={{ minWidth: '200px' }}>
          <Text type="subtle" size="sm" style={{ display: 'block', fontWeight: 400 }}>
            {t('configuration')}
          </Text>
          {configName ? (
            <Link to={`/tenants/${tenantId}/node-configs/${nodeGroup.configId}`}>
              <BuildOutlined /> {configName}
              {revisionNumber && ` · #${revisionNumber}`}
            </Link>
          ) : (
            <Text size="md">-</Text>
          )}
        </Container>
        <Container style={{ flex: 1 }} gap="xs" />
        <Container horizontal>
          <Container gap="xs" style={{ minWidth: '100px' }}>
            <Text type="subtle" size="sm" style={{ display: 'block', fontWeight: 400 }}>
              {t('nodeCount')}
            </Text>
            <Text data-testid="node-group-node-count" size="md">
              {nodeGroup.nodeCount}
            </Text>
          </Container>
          <Container gap="xs" style={{ minWidth: '150px' }}>
            <Text type="subtle" size="sm" style={{ display: 'block', fontWeight: 400 }}>
              {t('createdUtc')}
            </Text>
            <Text size="md">{dayjs(nodeGroup.dateCreated).utc().format('DD MMM YYYY HH:mm:ss')}</Text>
          </Container>
        </Container>
        {!isEditing ? (
          <Container gap="xs" style={{ minWidth: '30%' }}>
            <Text type="subtle" size="sm" style={{ display: 'block', fontWeight: 400 }}>
              {t('description')}
            </Text>
            <Text size="md" style={{ maxWidth: '95%' }}>
              {nodeGroup.description || t('noDescriptionProvided')}
            </Text>
          </Container>
        ) : null}
      </Flex>
    </>
  );
}

type ActionsDropdownProps = {
  onToggleNodeMode: () => void;
  onToggleNodeState: () => void;
  onApplyConfiguration: () => void;
  onUpdateFleetConfig: () => void;
  isApplyingConfig: boolean;
};

function ActionsDropdown({
  onToggleNodeMode,
  onToggleNodeState,
  onApplyConfiguration,
  onUpdateFleetConfig,
  isApplyingConfig,
}: ActionsDropdownProps) {
  const { t } = useMimicTranslation('fleets');
  const isAdmin = useAuthorization('admin');

  if (!isAdmin) return null;

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case 'apply-configuration':
        onApplyConfiguration();
        break;
      case 'update-configuration':
        onUpdateFleetConfig();
        break;
      case 'toggle-node-mode':
        onToggleNodeMode();
        break;
      case 'toggle-node-state':
        onToggleNodeState();
        break;
      default:
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      label: t('applyConfiguration'),
      key: 'apply-configuration',
    },
    {
      label: t('updateConfiguration'),
      key: 'update-configuration',
    },
    {
      label: t('toggleNodeState'),
      key: 'toggle-node-state',
    },
    {
      label: t('toggleNodeMode'),
      key: 'toggle-node-mode',
    },
  ];

  return (
    <Dropdown menu={{ items, onClick: handleMenuClick, className: 'fleet-actions-menu' }}>
      <Button type="primary" dataTestId="fleet-actions" loading={isApplyingConfig}>
        {t('actions')} <DownOutlined />
      </Button>
    </Dropdown>
  );
}
